import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { countriesWithProvinces } from '@/data/constants';
import {
  mentorMenuItems,
  menteeMenuItems,
  commonMenuItemsGroup,
  commonInternalRoutes,
} from '@/data/mainNavigationItems';
import { MenuItem } from '@/components/utils/types';
import { AvailabilitySlotShape } from '@/data/types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Returns true if the given country uses provinces instead of states.
 * @param country - The name of the country.
 */
export function hasProvinces(country: string): boolean {
  return countriesWithProvinces.has(country);
}

/**
 * Finds the menu label corresponding to a route, including support for dynamic routes
 * @param route - The current route path
 * @returns The label for the matching menu item or null if not found
 */
export const findMenuLabel = (route: string): string | null => {
  const allMenuItems = [
    ...mentorMenuItems,
    ...menteeMenuItems,
    ...commonMenuItemsGroup,
    ...commonInternalRoutes,
  ];

  // Helper function to check if a route matches a pattern with dynamic segments
  const isRouteMatch = (pattern: string, actualRoute: string): boolean => {
    // If exact match, return immediately
    if (pattern === actualRoute) return true;

    // Check for dynamic segments denoted by [param] syntax
    if (pattern.includes('[') && pattern.includes(']')) {
      // Convert pattern to regex by replacing [anyParam] with a regex pattern
      // that captures any segment except slashes
      const regexPattern = pattern.replace(/\[([^\]]+)\]/g, '([^/]+)');

      // Escape special regex characters except for the parentheses we added
      const escapedPattern = regexPattern.replace(/[.+?^${}()|]/g, '\\$&');

      // Create the regex with start/end anchors
      const regex = new RegExp(`^${escapedPattern}$`);

      return regex.test(actualRoute);
    }

    return false;
  };

  const findLabel = (items: MenuItem[]): string | null => {
    for (const item of items) {
      // Check if the current route matches the menu item's route
      if (isRouteMatch(item.route, route)) {
        return item.label;
      }

      // For nested routes, check if the current route starts with the menu item's route
      // and contains dynamic segments
      if (
        item.route.includes('[') &&
        route.startsWith(item.route.split('[')[0])
      ) {
        return item.label;
      }

      // Recursively check children
      if (item.children) {
        const foundLabel = findLabel(item.children);
        if (foundLabel) return foundLabel;
      }
    }
    return null;
  };

  return findLabel(allMenuItems);
};

export const getLastStringFromSeparator = (
  str: string,
  separator: string
): string => {
  const parts = str.split(separator);

  return parts[parts.length - 1].trim();
};

// Helper function to group availability slots by displayDate
export async function groupSlotsByDate(
  slots: AvailabilitySlotShape[]
): Promise<Record<string, AvailabilitySlotShape[]>> {
  return slots.reduce(
    (acc, slot) => {
      const dateKey = slot.displayDate || '';
      if (!(dateKey in acc)) {
        acc[dateKey] = [];
      }
      acc[dateKey]!.push(slot);
      return acc;
    },
    {} as Record<string, AvailabilitySlotShape[]>
  );
}

export function getSignOutRedirectUrl() {
  const clientId = `${process.env.NEXT_PUBLIC_AUTH_USER_POOL_CLIENT_ID}`;
  const logoutUri = `${process.env.NEXT_PUBLIC_APP_URL}`;
  const cognitoDomain = `${process.env.NEXT_PUBLIC_AUTH_COGNITO_PUBLIC_APP_DOMAIN}`;

  return `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
}
